import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Rating,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowOutward as ArrowOutwardIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  AccessTime as DurationIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getImageUrl, FALLBACK_IMAGE } from '../../utils/imageUtils';

// ============ Styled Components ============

const CardRoot = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  flexShrink: 0,
  backgroundColor: '#fff',
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.12)}`,
  },
}));

const CardImageWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '200px',
  overflow: 'hidden',
});

const CardImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
  '&:hover': {
    transform: 'scale(1.08)',
  },
});

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: theme.palette.text.primary,
  lineHeight: 1.3,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const CardMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.85rem',
}));

const Badge = styled(Box)(({ bgcolor, textcolor }) => ({
  position: 'absolute',
  top: 12,
  left: 12,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  borderRadius: '8px',
  fontWeight: 700,
  fontSize: '0.75rem',
  backdropFilter: 'blur(4px)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
}));

// ============ Helper Functions ============

export const getRating = (tour) => {
  if (tour.rating) return Number(tour.rating);
  if (tour._id) {
    const idStr = String(tour._id);
    const hash = idStr.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return 3.5 + (hash % 15) / 10;
  }
  return 4.0;
};

export const getCapacity = (tour) => {
  if (tour.maxPeople) return `Up to ${tour.maxPeople}`;
  if (tour.max_people) return `Up to ${tour.max_people}`;
  return null;
};

export const getLocation = (tour) => {
  return tour.destination || tour.location || null;
};

export const getDuration = (tour) => {
  return tour.duration || null;
};

// ============ TourCard Component ============

const TourCard = ({
  tour,
  minWidth = '300px',
  width = '300px',
  showDuration = true,
  showBadge = false,
  badgeLabel = '',
  badgeColor = 'rgba(255, 193, 7, 0.95)',
  badgeTextColor = '#1a1a2e',
  badgeIcon = null,
  variant = 'carousel', // 'carousel' | 'grid'
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const rating = getRating(tour);
  const capacity = getCapacity(tour);
  const location = getLocation(tour);
  const duration = getDuration(tour);

  return (
    <CardRoot
      sx={{
        minWidth: variant === 'grid' ? '100%' : minWidth,
        width: variant === 'grid' ? '100%' : width,
      }}
      onClick={() => navigate(`/booking?tourId=${tour._id}`)}
    >
      <CardImageWrapper>
        <CardImage
          src={getImageUrl(tour.imageUrl) || FALLBACK_IMAGE}
          alt={tour.title}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = FALLBACK_IMAGE;
          }}
        />
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '50%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)',
            zIndex: 1,
          }}
        />

        {/* Custom Badge (e.g., "Best Seller" or "Exclusive") */}
        {showBadge && badgeLabel && (
          <Badge bgcolor={badgeColor} textcolor={badgeTextColor}>
            {badgeIcon && <Box sx={{ fontSize: 14, display: 'flex' }}>{badgeIcon}</Box>}
            {badgeLabel}
          </Badge>
        )}
      </CardImageWrapper>

      <CardContent>
        <CardTitle>{tour.title}</CardTitle>

        {/* Rating Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Rating
            value={rating}
            readOnly
            precision={0.5}
            size="small"
            sx={{ '& .MuiRating-iconFilled': { color: '#f59e0b' } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {rating.toFixed(1)}
          </Typography>
        </Box>

        {/* Meta Info Rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          {duration && showDuration && (
            <CardMeta>
              <DurationIcon sx={{ fontSize: 16, opacity: 0.7 }} />
              <Typography variant="caption" color="text.secondary">{duration}</Typography>
            </CardMeta>
          )}
          {capacity && (
            <CardMeta>
              <PeopleIcon sx={{ fontSize: 16, opacity: 0.7 }} />
              <Typography variant="caption" color="text.secondary">{capacity} People</Typography>
            </CardMeta>
          )}
          {location && (
            <CardMeta>
              <LocationIcon sx={{ fontSize: 16, opacity: 0.7 }} />
              <Typography variant="caption" color="text.secondary">{location}</Typography>
            </CardMeta>
          )}
        </Box>

        {/* CTA */}
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowOutwardIcon fontSize="small" />}
          sx={{
            mt: 1,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            borderColor: alpha(theme.palette.primary.main, 0.3),
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/booking?tourId=${tour._id}`);
          }}
        >
          Book Now
        </Button>
      </CardContent>
    </CardRoot>
  );
};

export default TourCard;